import { ChatMiddlewareType } from '@indigohive/cogfy-types'
import { Fragment, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Menu, OverlayContent } from '../../../../../../components'
import { ChatMiddlewareIcon } from '../../../../../../components/ChatMiddlewareIcon'
import { SearchInput } from '../../../../../../pages/CollectionPageV2/components/SearchInput'

export type AddMiddlewareMenuProps = {
  anchorEl?: HTMLElement | null
  onClose: () => void
  onAddMiddlewareClick: (type: ChatMiddlewareType) => void
  searchMiddlewareName: string
  setSearchMiddlewareName: (name: string) => void
}

const MIDDLEWARE_OPTIONS: Partial<Record<ChatMiddlewareType, { label: ReactNode }>>[] = [
  {
    rateLimiting: { label: 'Rate limiting' },
    reply: { label: 'Reply' },
    http: { label: 'HTTP' },
    group: { label: 'Group' },
    downstreamFallback: { label: 'Downstream fallback' },
    upstreamFallback: { label: 'Upstream fallback' },
    stop: { label: 'Stop' },
    echo: { label: 'Echo' },
    errorHandler: { label: 'Error handler' },
    filterOutput: { label: 'Filter output' },
    sentry: { label: 'Sentry' }
  },
  {
    generateSpeech: { label: 'Generate speech' },
    transcribeAudio: { label: 'Transcribe audio' },
    'cpqdAsr.generateSpeech': { label: 'Generate speech (CPQD)' },
    'elevenLabs.generateSpeech': { label: 'Generate speech (Eleven Labs)' },
    'lemonfox.generateSpeech': { label: 'Generate speech (Lemon Fox)' },
    'lemonfox.transcribeAudio': { label: 'Transcribe audio (Lemon Fox)' },
    'hamsa.generateSpeech': { label: 'Generate speech (Hamsa)' },
    'hamsa.transcribeAudio': { label: 'Transcribe audio (Hamsa)' }
  },
  {
    'openai.generateStandaloneQuestion': { label: 'OpenAI Generate Standalone Question' },
    'openai.router': { label: 'OpenAI Router' }
  },
  {
    'bedrock.router': { label: 'AWS Bedrock Router' },
    'bedrock.generateStandaloneQuestion': { label: 'AWS Bedrock Generate Standalone Question' }
  },
  {
    instructions: { label: 'Instructions' },
    history: { label: 'History' },
    'cogfy.collectionRetrieval': { label: 'Collection retrieval' },
    'typesense.retrieval': { label: 'Typesense retrieval' },
    scheduleMessage: { label: 'Schedule message' },
    tool: { label: 'Tool' },
    maritaca: { label: 'Maritaca' },
    mcpTools: { label: 'MCP Tools' },
    anthropic: { label: 'Anthropic' },
    azure: { label: 'Azure' },
    bedrock: { label: 'AWS Bedrock' },
    deepseek: { label: 'DeepSeek' },
    googleAIStudio: { label: 'Google AI Studio' },
    openai: { label: 'OpenAI' },
    grok: { label: 'Grok' },
    groq: { label: 'Groq' }
  },
  {
    consensus: { label: 'Consensus' }
  },
  {
    whatsapp: { label: 'WhatsApp' }
  }
]

export function AddChatMiddlewareContextMenu (props: AddMiddlewareMenuProps) {
  const { anchorEl, onClose, onAddMiddlewareClick, searchMiddlewareName, setSearchMiddlewareName } = props
  const { t } = useTranslation()
  const filteredMiddlewareGroups = MIDDLEWARE_OPTIONS.map((options) =>
    Object.entries(options).filter(([type]) => type.toLowerCase().includes(searchMiddlewareName.toLowerCase()))
  ).filter((group) => group.length > 0)

  return (
    <OverlayContent
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ horizontal: 'right' }}
      onClose={onClose}
    >
      <Menu className="max-h-96">
        <li className="menu-title">{t('Add middleware')}</li>
        <SearchInput
          searchName={searchMiddlewareName}
          onNameChange={(name) => setSearchMiddlewareName(name)}
        />
        {filteredMiddlewareGroups.length > 0 && (
          filteredMiddlewareGroups.map((group, index) => (
            <Fragment key={index}>
              {group.map(([type, option]) => (
                <li key={type} onClick={() => onAddMiddlewareClick(type as ChatMiddlewareType)}>
                  <a>
                    <ChatMiddlewareIcon type={type as ChatMiddlewareType} size={16} />
                    {option.label}
                  </a>
                </li>
              ))}
              {index < filteredMiddlewareGroups.length - 1 && !searchMiddlewareName && (
                <div className="divider my-0 py-0" />
              )}
            </Fragment>
          ))
        )}
        {filteredMiddlewareGroups.length === 0 && (
          <li className="text-gray-500 p-2">{t('No middlewares found')}</li> // Show only when no matches
        )}
      </Menu>
    </OverlayContent>
  )
}
